import { useEffect, useRef, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceArea,
} from "recharts";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, TrendingDown, Minus, Activity, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BAND_COLORS = {
  normal: "#0f7a3b",
  mild: "#c68a1a",
  moderate: "#c95e10",
  high: "#b3261e",
};

/** Full "Progress over time" section: one chart per series. */
export function LongitudinalDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ series: [], total_reports: 0, span_days: 0 });
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    api.get("/reports/timeline").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const withPoints = data.series.filter((s) => s.points?.length > 0);

  const downloadPdf = async () => {
    if (!captureRef.current || exporting) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      // Capture the section at a nice DPR for crisp charts
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 36;

      // Header
      pdf.setFillColor(15, 90, 168);
      pdf.rect(0, 0, pageW, 70, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.text("Manodayam · Longitudinal progress report", margin, 34);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      const stamp = new Date().toLocaleString();
      pdf.text(
        `${user?.name || "Patient"}  ·  Generated ${stamp}  ·  ${data.total_reports} screenings`,
        margin, 54,
      );

      // Image body: fit width, paginate on height
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      let y = 90;
      const available = pageH - y - margin;

      if (imgH <= available) {
        pdf.addImage(imgData, "PNG", margin, y, imgW, imgH);
      } else {
        // Multi-page: slice the canvas
        const pxPerPt = canvas.width / imgW;
        const pageBodyH = pageH - y - margin;
        const slicePxH = Math.floor(pageBodyH * pxPerPt);
        let sy = 0;
        let first = true;
        while (sy < canvas.height) {
          const sliceH = Math.min(slicePxH, canvas.height - sy);
          const slice = document.createElement("canvas");
          slice.width = canvas.width;
          slice.height = sliceH;
          const ctx = slice.getContext("2d");
          ctx.drawImage(canvas, 0, sy, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
          const sliceData = slice.toDataURL("image/png");
          if (!first) {
            pdf.addPage();
            y = margin;
          }
          const sliceHpt = (sliceH * imgW) / canvas.width;
          pdf.addImage(sliceData, "PNG", margin, y, imgW, sliceHpt);
          sy += sliceH;
          first = false;
        }
      }

      // Footer disclaimer
      pdf.setTextColor(120, 120, 120);
      pdf.setFontSize(8);
      pdf.text(
        "This report is a personal summary generated by Manodayam VAB. Please share it with your clinician for interpretation.",
        margin, pageH - 16,
      );

      const safeName = (user?.name || "patient").replace(/\s+/g, "_").toLowerCase();
      pdf.save(`manodayam_progress_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("Your progress PDF is ready.");
    } catch (e) {
      console.error(e);
      toast.error("Could not export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (!withPoints.length) {
    return (
      <section
        className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-8 text-center"
        data-testid="longitudinal-empty"
      >
        <Activity size={22} className="mx-auto text-[color:var(--mano-ink-soft)]" />
        <div className="font-display text-lg mt-3">Track how you feel over time</div>
        <p className="text-sm text-[color:var(--mano-ink-soft)] mt-1">
          After a couple of screenings, your progress trends will appear here.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="longitudinal-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <span className="uppercase-label">Progress over time</span>
          <h2 className="font-display text-2xl mt-1">Your longitudinal assessments</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[color:var(--mano-ink-soft)] hidden sm:inline">
            {data.total_reports} screening{data.total_reports === 1 ? "" : "s"}
            {data.span_days > 0 ? ` · spanning ${data.span_days} day${data.span_days === 1 ? "" : "s"}` : ""}
          </span>
          <button
            data-testid="longitudinal-download-pdf"
            onClick={downloadPdf}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-60"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exporting ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>

      <div ref={captureRef} className="grid md:grid-cols-2 gap-4 bg-[color:var(--mano-bg)] p-1 rounded-2xl">
        {withPoints.map((s) => (
          <SeriesChart key={s.key} series={s} />
        ))}
      </div>
    </section>
  );
}

function SeriesChart({ series }) {
  const rows = series.points.map((p) => ({
    at: p.at,
    ts: new Date(p.at).getTime(),
    label: new Date(p.at).toLocaleDateString(undefined, { day: "numeric", month: "short" }),
    score: p.score,
    band: p.band,
  }));

  const first = rows[0]?.score;
  const last = rows[rows.length - 1]?.score;
  const delta = last != null && first != null ? last - first : null;
  const improving = series.invert ? (delta > 0) : (delta < 0);
  const worsening = series.invert ? (delta < 0) : (delta > 0);

  const max = series.max_score || Math.max(...rows.map((r) => r.score), 10);
  const cutoffs = series.cutoffs || [];
  // Build band zones for coloured background reference areas
  const zones = buildZones(cutoffs, max, series.invert);

  return (
    <div
      className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-4 lg:p-5"
      data-testid={`series-chart-${series.key}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="uppercase-label">{series.kind === "voice" ? "Voice screening" : "Questionnaire"}</div>
          <div className="font-display text-lg mt-0.5">{series.name}</div>
          <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5">
            {rows.length} attempt{rows.length === 1 ? "" : "s"}
          </div>
        </div>
        {delta != null && rows.length > 1 && (
          <div
            className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
              improving
                ? "bg-emerald-50 text-emerald-700"
                : worsening
                ? "bg-rose-50 text-rose-700"
                : "bg-slate-100 text-slate-600"
            }`}
            data-testid={`series-trend-${series.key}`}
          >
            {improving ? (
              <>
                <TrendingDown size={12} /> improving
              </>
            ) : worsening ? (
              <>
                <TrendingUp size={12} /> worsening
              </>
            ) : (
              <>
                <Minus size={12} /> steady
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ width: "100%", height: 200 }} className="mt-3">
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e6ebf1" />
            {zones.map((z, i) => (
              <ReferenceArea key={i} y1={z.from} y2={z.to} fill={z.color} fillOpacity={0.08} />
            ))}
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b6b6b" }} />
            <YAxis
              domain={[0, max]}
              tick={{ fontSize: 11, fill: "#6b6b6b" }}
              width={40}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div className="rounded-lg bg-white border border-[color:var(--mano-border)] px-3 py-2 text-xs shadow-sm">
                    <div className="font-semibold">{p.label}</div>
                    <div className="text-[color:var(--mano-ink-soft)]">Score: {p.score}</div>
                    <div className="capitalize" style={{ color: BAND_COLORS[p.band] || "#4a5d53" }}>
                      Band: {p.band}
                    </div>
                  </div>
                );
              }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#0f5aa8"
              strokeWidth={2.5}
              dot={({ cx, cy, payload }) => (
                <circle
                  cx={cx}
                  cy={cy}
                  r={5}
                  fill={BAND_COLORS[payload.band] || "#0f5aa8"}
                  stroke="#fff"
                  strokeWidth={2}
                />
              )}
              activeDot={{ r: 7 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex items-center gap-3 text-[10px] text-[color:var(--mano-ink-soft)] flex-wrap">
        <BandDot color={BAND_COLORS.normal} label="normal" />
        <BandDot color={BAND_COLORS.mild} label="mild" />
        <BandDot color={BAND_COLORS.moderate} label="moderate" />
        <BandDot color={BAND_COLORS.high} label="high" />
      </div>
    </div>
  );
}

function BandDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

/** Given cutoffs [c1,c2,c3] and max score, produce coloured horizontal zones.
 * By default score-going-up means worse (normal→high). If invert=true,
 * score-going-down means worse (e.g. MoCA where lower = more impaired). */
function buildZones(cutoffs, max, invert) {
  if (!cutoffs?.length) return [];
  const bounds = [0, ...cutoffs, max];
  const bands = ["normal", "mild", "moderate", "high"];
  const seq = invert ? bands.slice().reverse() : bands;
  const zones = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    zones.push({
      from: bounds[i],
      to: bounds[i + 1],
      color: BAND_COLORS[seq[i] || "normal"],
    });
  }
  return zones;
}
